"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import { PortableText } from "@portabletext/react";
import Navbar from "@/app/components/Navbar";
import Footer from "@/app/components/Footer";
import { client } from "@/sanity/lib/client";
import { urlForImage } from "@/sanity/lib/image";
import dynamic from "next/dynamic";
import NewsletterForm from "@/app/components/NewsletterForm";

const AmbientCanvas = dynamic(
  () => import("@/components/three/AmbientCanvas"),
  { ssr: false, loading: () => null }
);

interface SanityPostFull {
  _id: string;
  title: string;
  slug: { current: string };
  mainImage: any;
  publishedAt: string;
  excerpt: string;
  body: any[];
  categories: { title: string }[];
  author: { name: string; image: any };
  tags: string[];
}

interface SanityRelated {
  _id: string;
  title: string;
  slug: { current: string };
  category: string;
  publishedAt: string;
}

const portableTextComponents = {
  types: {
    image: ({ value }: any) => {
      if (!value?.asset?._ref) return null;
      return (
        <div className="my-8 overflow-hidden" style={{ border: "1px solid rgba(201,168,76,0.1)" }}>
          <img
            src={urlForImage(value).url()}
            alt={value.alt || ""}
            className="w-full h-auto opacity-90"
          />
        </div>
      );
    },
  },
  block: {
    h2: ({ children }: any) => <h2 className="text-2xl font-light mt-10 mb-4" style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}>{children}</h2>,
    h3: ({ children }: any) => <h3 className="text-xl font-light mt-8 mb-3" style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}>{children}</h3>,
    normal: ({ children }: any) => <p className="mb-5 leading-relaxed" style={{ color: "rgba(245,240,232,0.75)" }}>{children}</p>,
    blockquote: ({ children }: any) => (
      <blockquote className="border-l-2 pl-6 my-8 italic" style={{ borderColor: "rgba(201,168,76,0.4)", color: "rgba(245,240,232,0.55)", fontFamily: "'Cormorant', serif" }}>
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }: any) => <ul className="list-disc pl-6 mb-5 space-y-2" style={{ color: "rgba(245,240,232,0.75)" }}>{children}</ul>,
    number: ({ children }: any) => <ol className="list-decimal pl-6 mb-5 space-y-2" style={{ color: "rgba(245,240,232,0.75)" }}>{children}</ol>,
  },
  marks: {
    link: ({ children, value }: any) => {
      const rel = !value.href.startsWith("/") ? "noreferrer noopener" : undefined;
      return (
        <a 
          href={value.href} 
          rel={rel} 
          className="underline underline-offset-4 decoration-[rgba(201,168,76,0.4)] hover:text-[#C9A84C] transition-colors"
          style={{ color: "#C9A84C" }}
        >
          {children}
        </a>
      );
    },
    strong: ({ children }: any) => <strong style={{ color: "#F5F0E8" }}>{children}</strong>,
  },
};

function PostBottomCTA() {
  return (
    <div 
      className="mt-16 p-10 relative overflow-hidden text-center"
      style={{ 
        background: "rgba(201,168,76,0.02)",
        border: "1px solid rgba(201,168,76,0.1)" 
      }}
    >
      <div className="relative z-10 max-w-xl mx-auto">
        <h3 className="text-2xl font-light mb-4" style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}>
          Join the <span style={{ color: "#C9A84C", fontStyle: "italic" }}>Aurelius Insider</span>
        </h3>
        <p className="text-sm mb-8" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>
          Get weekly insights on Instagram automation, growth strategies, and exclusive product updates.
        </p>
        <NewsletterForm />
      </div>
    </div>
  );
}

import DesignShowcase from "@/app/components/DesignShowcase";

function AdPlaceholder({ className = "", minHeight }: { className?: string; minHeight?: number }) {
  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{ 
        border: "1px solid rgba(201,168,76,0.15)", 
        minHeight: minHeight ?? 350 
      }}
    >
      <DesignShowcase />
    </div>
  );
}

function RelatedPostCard({ post }: { post: SanityRelated }) {
  const dateStr = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";

  return (
    <Link href={`/blog/${post.slug.current}`}>
      <div
        className="p-4 mb-3 group transition-all"
        style={{ border: "1px solid rgba(201,168,76,0.08)", background: "rgba(255,255,255,0.01)" }}
        onMouseEnter={e => (e.currentTarget.style.borderColor = "rgba(201,168,76,0.25)")}
        onMouseLeave={e => (e.currentTarget.style.borderColor = "rgba(201,168,76,0.08)")}
      >
        <span className="text-[9px] tracking-widest uppercase px-2 py-0.5 mb-2 inline-block" style={{ background: "rgba(201,168,76,0.1)", color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>
          {post.category}
        </span>
        <h4 className="text-sm font-light leading-snug mb-1 group-hover:text-[#C9A84C] transition-colors" style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}>
          {post.title}
        </h4>
        {dateStr && (
          <p className="text-[10px]" style={{ color: "rgba(245,240,232,0.25)", fontFamily: "'Montserrat', sans-serif" }}>{dateStr}</p>
        )}
      </div>
    </Link>
  );
}

export default function BlogPostClient({ slug }: { slug: string }) {
  const [post, setPost] = useState<SanityPostFull | null>(null);
  const [related, setRelated] = useState<SanityRelated[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      if (!slug) return;
      setLoading(true);
      try {
        const query = `*[_type == "post" && slug.current == $slug][0] {
          _id, title, slug, mainImage, publishedAt, excerpt, body, tags,
          "categories": categories[]->{title},
          "author": author->{name, image}
        }`;
        const result = await client.fetch(query, { slug });
        
        if (result) {
          setPost(result);
          
          // Fetch related (same category)
          if (result.categories?.[0]) {
            const relQuery = `*[_type == "post" && slug.current != $slug && "${result.categories[0].title}" in categories[]->title] | order(publishedAt desc) [0...3] {
              _id, title, slug, publishedAt,
              "category": categories[0]->title
            }`;
            const relResult = await client.fetch(relQuery, { slug });
            setRelated(relResult);
          }
        }
      } catch (err) {
        console.error("Sanity fetch error:", err);
      }
      setLoading(false);
    }
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center" style={{ background: "#0A0908" }}>
          <div className="w-6 h-6 rounded-full border-2 animate-spin" style={{ borderColor: "rgba(201,168,76,0.2)", borderTopColor: "#C9A84C" }} />
        </main>
        <Footer />
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex flex-col items-center justify-center" style={{ background: "#0A0908" }}>
          <p className="text-sm mb-4" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>Article not found.</p>
          <Link href="/blog" className="text-xs tracking-widest uppercase" style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>← Back to Blog</Link>
        </main>
        <Footer />
      </>
    );
  }

  const dateStr = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "Recently Published";

  const coverImageUrl = post.mainImage ? urlForImage(post.mainImage).url() : null;

  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    url: `https://www.aureliusco.in/blog/${post.slug.current}`,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    author: {
      "@type": "Person",
      name: post.author?.name || "Aurelius & Co",
    },
    publisher: {
      "@type": "Organization",
      "@id": "https://www.aureliusco.in/#organization",
      name: "Aurelius & Co",
      logo: { "@type": "ImageObject", url: "https://www.aureliusco.in/logo.png" },
    },
    image: coverImageUrl || "https://www.aureliusco.in/og-image.png",
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.aureliusco.in/blog/${post.slug.current}` },
    keywords: post.tags?.join(", "),
    articleSection: post.categories?.[0]?.title,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.aureliusco.in" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.aureliusco.in/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: `https://www.aureliusco.in/blog/${post.slug.current}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPostingSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="min-h-screen pt-28 pb-20 px-6" style={{ background: "#0A0908" }}>
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8">
            <Link href="/blog" className="text-xs tracking-widest uppercase flex items-center gap-2 w-fit transition-colors hover:text-[#C9A84C]" style={{ color: "rgba(245,240,232,0.35)", fontFamily: "'Montserrat', sans-serif" }}>
              <span>←</span> Blog
            </Link>
          </motion.div>

          <div className="flex flex-col lg:flex-row gap-12">
            {/* Main content */}
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex-1 min-w-0"
            >
              {/* Category + meta */}
              <div className="flex items-center gap-3 mb-5">
                <span className="text-xs tracking-widest uppercase px-3 py-1" style={{ background: "rgba(201,168,76,0.12)", color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>
                  {post.categories?.[0]?.title || "Article"}
                </span>
                {post.tags?.slice(0, 2).map(tag => (
                  <span key={tag} className="text-[9px] uppercase tracking-wider px-2 py-0.5" style={{ background: "rgba(201,168,76,0.06)", color: "rgba(201,168,76,0.4)", fontFamily: "'Montserrat', sans-serif" }}>{tag}</span>
                ))}
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-light leading-tight mb-5" style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}>
                {post.title}
              </h1>

              <p className="text-base leading-relaxed mb-6" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Cormorant', serif", fontStyle: "italic" }}>
                {post.excerpt}
              </p>

              <div className="flex items-center gap-4 mb-8 pb-6" style={{ borderBottom: "1px solid rgba(201,168,76,0.1)" }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium" style={{ background: "rgba(201,168,76,0.15)", color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>
                  {post.author?.name?.charAt(0).toUpperCase() || "A"}
                </div>
                <div>
                  <p className="text-xs font-medium" style={{ color: "rgba(245,240,232,0.7)", fontFamily: "'Montserrat', sans-serif" }}>{post.author?.name || "AureliusCo Team"}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-[10px]" style={{ color: "rgba(245,240,232,0.3)", fontFamily: "'Montserrat', sans-serif" }}>{dateStr}</p>
                    <span style={{ color: "rgba(201,168,76,0.2)" }}>·</span>
                    <p className="text-[10px]" style={{ color: "rgba(245,240,232,0.3)", fontFamily: "'Montserrat', sans-serif" }}>5 min read</p>
                  </div>
                </div>
              </div>

              {/* Cover image */}
              {coverImageUrl && (
                <div className="mb-8 overflow-hidden" style={{ border: "1px solid rgba(201,168,76,0.1)" }}>
                  <img src={coverImageUrl} alt={post.title} className="w-full max-h-80 object-cover opacity-80" />
                </div>
              )}

              {/* Rich content from Sanity */}
              <div className="sanity-content" style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.92rem", lineHeight: "1.8" }}>
                <PortableText value={post.body} components={portableTextComponents} />
              </div>

              {/* Bottom CTA */}
              <PostBottomCTA />
            </motion.article>

            {/* Sidebar */}
            <motion.aside
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="lg:w-72 shrink-0"
            >
              <AdPlaceholder className="mb-6 w-full" minHeight={350} />

              {related.length > 0 && (
                <div className="mb-6">
                  <p className="text-[9px] tracking-[0.25em] uppercase mb-4" style={{ color: "rgba(201,168,76,0.6)", fontFamily: "'Montserrat', sans-serif" }}>
                    Related Articles
                  </p>
                  {related.map(r => <RelatedPostCard key={r._id} post={r} />)}
                </div>
              )}

              <div className="mt-6">
                <Link
                  href="/blog"
                  className="block text-center text-xs tracking-widest uppercase py-3 px-4 transition-all"
                  style={{ border: "1px solid rgba(201,168,76,0.2)", color: "rgba(201,168,76,0.7)", fontFamily: "'Montserrat', sans-serif" }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = "rgba(201,168,76,0.4)")}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = "rgba(201,168,76,0.2)")}
                >
                  ← All Articles
                </Link>
              </div>
            </motion.aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
